'use client'

import { useEffect, useMemo, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import Image from 'next/image'
import {
  ArrowLeft, BadgePercent, Check, ChevronDown, CircleCheck, FileText, Gift, HandCoins,
  Info, LoaderCircle, MapPinPlus, Package, PackageX, ShoppingBag, Store, Wallet, X,
} from 'lucide-react'
import { useCart, useAddress, useCustomFields, useCustomerDetails, useDelivery, useOrderTotal, usePayment, useCheckoutSubmit } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice, getBuyXTriggerCount, getBuyXGetYRewardRule } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import ProcessingState from '@/components/elements/ProcessingState'
import CheckoutCard from './CheckoutCard'
import DiscountTabs from './DiscountTabs'
import TermsDrawer from './TermsDrawer'
import Qpay from '@/assets/qpay.svg'
import TransferIcon from '@/assets/transfer.png'
import Storepay from '@/assets/storepay.png'
import Pocket from '@/assets/pocket.png'
import Lend from '@/assets/lendmn.png'
import Omniway from '@/assets/omniway.png'
import Booklo from '@/assets/booklo.jpg'

const inputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.13)',
  color: 'white',
}
const cardStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.035)',
  border: '1px solid rgba(255,255,255,0.07)',
}

function FieldLabel({ children, required }: { children: React.ReactNode, required?: boolean }) {
  return (
    <div className="text-[12px] font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.75)' }}>
      {children}
      {required && <span className="ml-1" style={{ color: '#fda4af' }}>*</span>}
    </div>
  )
}

function DarkInput({ label, required, ...props }: any) {
  return (
    <label className="block">
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <input
        {...props}
        className={`w-full h-11 px-4 rounded-xl text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30 ${props.className || ''}`}
        style={{ ...inputStyle, ...(props.style || {}) }}
      />
    </label>
  )
}

function DarkTextarea({ label, required, ...props }: any) {
  return (
    <label className="block">
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <textarea
        {...props}
        className={`w-full rounded-xl px-4 py-3 text-[13.5px] outline-none focus:ring-0 resize-none leading-6 placeholder:text-white/30 scrollbar-dark ${props.className || ''}`}
        style={{ ...inputStyle, ...(props.style || {}) }}
      />
    </label>
  )
}

function DarkSelect({ label, items, value, onChange, required, placeholder }: any) {
  return (
    <label className="block">
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <div className="relative">
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 px-4 pr-9 rounded-xl text-[13.5px] outline-none focus:ring-0 appearance-none cursor-pointer"
          style={{ ...inputStyle, colorScheme: 'dark' }}
        >
          <option value="">{placeholder || 'Сонгох...'}</option>
          {(items || []).map((i: any) => (
            <option key={i.value} value={i.value}>{i.label}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none" style={{ color: 'rgba(255,255,255,0.4)' }} />
      </div>
    </label>
  )
}

function ProgressRow({ label, pct, barColor }: { label: React.ReactNode, pct: number, barColor: string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11.5px] gap-3">
        <div className="min-w-0 truncate">{label}</div>
        <span className="tabular-nums shrink-0" style={{ color: 'rgba(255,255,255,0.55)' }}>{Math.min(100, Math.round(pct))}%</span>
      </div>
      <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
      </div>
    </div>
  )
}

function Section({ num, title, children }: { num?: string, title: string, children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-4">
        {num && <span className="text-[11px] font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>{num}</span>}
        <h2 className="text-[18px] font-semibold tracking-[-0.01em] text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{title}</h2>
      </div>
      {children}
    </section>
  )
}

function Radio({ active, disabled, onClick, title, sub, right }: { active: boolean, disabled?: boolean, onClick: () => void, title: React.ReactNode, sub?: React.ReactNode, right?: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="w-full text-left flex items-center gap-3 p-4 rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed"
      style={{
        background: active ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.3)',
        border: active ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.13)',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <span
        className="w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0"
        style={active
          ? { background: 'white', border: '4px solid white', outline: '1px solid rgba(255,255,255,0.4)', outlineOffset: 2 }
          : { border: '1.5px solid rgba(255,255,255,0.3)' }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-medium text-white">{title}</div>
        {sub && <div className="text-[11.5px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{sub}</div>}
      </div>
      {right}
    </button>
  )
}

function AddressTypeTabs({ value, onChange }: { value: 'apartment' | 'house' | 'office', onChange: (v: 'apartment' | 'house' | 'office') => void }) {
  const tabs = [
    { v: 'apartment' as const, l: 'Орон сууц' },
    { v: 'house' as const, l: 'Хашаа байшин' },
    { v: 'office' as const, l: 'Оффис' },
  ]
  return (
    <div
      className="flex items-center gap-1 rounded-xl p-1"
      style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      {tabs.map(t => {
        const active = value === t.v
        return (
          <button
            key={t.v}
            type="button"
            onClick={() => onChange(t.v)}
            className="flex-1 h-9 rounded-lg text-[12.5px] font-medium transition-all cursor-pointer"
            style={active ? { background: 'rgba(255,255,255,0.08)', color: 'white' } : { background: 'transparent', color: 'rgba(255,255,255,0.55)' }}
          >
            {t.l}
          </button>
        )
      })}
    </div>
  )
}

function AddressTypeBadge({ type }: { type: string }) {
  const map: Record<string, string> = { apartment: 'Орон сууц', house: 'Хашаа байшин', office: 'Оффис' }
  return (
    <span
      className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded w-fit"
      style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.13)' }}
    >
      {map[type] || type}
    </span>
  )
}

export default function Checkout({ vendor, address, branches, customerAddress, subscription }: { vendor: any, address: any, branches: any, customerAddress: any, subscription: any }) {
  const pathname = usePathname()
  const router = useRouter()
  const { data: session } = useSession()
  const {
    cart, loading, initialLoading, isValidating, validateCart, canCheckout, amount, sale, orderDiscountAmount,
    allProducts, buyXGetYInfo, addRewardItem, availableOrderRewardItems, availableCouponRewardItems, activeOrderDiscount,
    rewardLoading, getRewardPrice, isRewardOutOfStock, getBuyXGetYRewardState, appliedCoupon, freeShippingProgress, cartCount,
    loyaltySaleOffsetAmount, loyaltyPreview,
  } = useCart()

  const addressApi = useAddress({ address, vendor, customerAddress, isLoggedIn: !!session?.user?.id })
  const {
    addressType, setAddressType, selectedAddress, setSelectedAddress, setAddressObj,
    setCity, setDuureg, setKhoroo, bair, setBair, toot, setToot, additionalAddress, setAdditionalAddress,
    houseNum, setHouseNum, street, setStreet, building, setBuilding, floor, setFloor, officeName, setOfficeName,
    selectedProvince, setSelectedProvince, selectedDistrict, setSelectedDistrict, selectedSubdistrict, setSelectedSubdistrict,
    provinces, districts, subdistricts, isAddressDeliverable, saveNewAddress,
  } = addressApi

  const products = cart?.products || []

  const deliveryApi = useDelivery({ vendor, branches })
  const { selectedDelivery, selectedBranch, setSelectedBranch, selectDelivery, hasDigitalProduct, hasDeliveryOption, hasPickupOption, showDeliverySection } = deliveryApi

  const { subPrice, validCouponDiscount, validGiftCardDiscount, loyaltyDiscount, validLoyaltyVoucherDiscount, loyaltyFreeShipping, deliveryPrice, totalPrice, isFreeOrder, depositAmount, balanceAmount, hasDeposit, payNowAmount } = useOrderTotal({ vendor, selectedDelivery })

  const deliveryRowPrice = useMemo(() => {
    if (hasDigitalProduct && products.every((p: any) => p.isDigital === true)) return 0
    if (loyaltyFreeShipping) return 0
    const dt = vendor?.deliveryType
    const vp = vendor?.deliveryPrice || 0
    let price = 0
    if (dt === 'constantDelivery') price = vp
    if (dt === 'minOrderAmount') price = subPrice >= (vendor?.minOrderAmount || 0) ? 0 : vp
    if (price > 0 && loyaltyPreview?.shippingDiscountPercent > 0) price = Math.max(0, price - Math.round(price * loyaltyPreview.shippingDiscountPercent / 100))
    return price
  }, [vendor, subPrice, products, hasDigitalProduct, loyaltyFreeShipping, loyaltyPreview])

  const paymentApi = usePayment({ vendor, products, isFreeOrder, hasDeposit })
  // 'Барааг авахдаа төлөх' means nothing is due now, so it supersedes the deposit.
  const effectiveDeposit = paymentApi.selectedPayment === 'onDelivery' ? 0 : depositAmount
  const { selectedPayment, storepayPhone, setStorepayPhone, lendPhone, setLendPhone, isMethodAllowedForCart, canPayOnDelivery, selectPayment } = paymentApi

  const customFieldsApi = useCustomFields()
  const {
    productValues: customFieldValues, orderValues: orderCustomFieldValues,
    setProductField, setOrderField, uploadProductFile, uploadOrderFile,
    isUploadingProductField, isUploadingOrderField,
  } = customFieldsApi
  const uploadingFiles = useMemo(() => ({ has: isUploadingProductField }), [isUploadingProductField])
  const uploadingOrderFiles = useMemo(() => ({ has: isUploadingOrderField }), [isUploadingOrderField])

  const customerDetailsApi = useCustomerDetails()
  const { fullName, setFullName, email, setEmail, phone, setPhone, additionalPhone, setAdditionalPhone } = customerDetailsApi

  const [openAddNewAddress, setOpenAddNewAddress] = useState(false)
  const [newAddressLoading, setNewAddressLoading] = useState(false)
  const [termsDrawerOpen, setTermsDrawerOpen] = useState(false)
  const [selectedTermsType, setSelectedTermsType] = useState<'delivery' | 'payment' | 'service' | null>(null)
  const [isTermsAccepted, setIsTermsAccepted] = useState(true)
  const [showMobileSummary, setShowMobileSummary] = useState(true)

  const { submit: submitCheckout, isPlacingOrder } = useCheckoutSubmit({
    vendor, subscription, customerDetails: customerDetailsApi, customFields: customFieldsApi,
    address: addressApi, payment: paymentApi, delivery: deliveryApi,
    totalPrice, payNowAmount, isFreeOrder, hasDigitalProduct, hasDeliveryOption, showDeliverySection,
  })

  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  const handleTermsClick = (type: 'delivery' | 'payment' | 'service') => {
    setSelectedTermsType(type)
    setTermsDrawerOpen(true)
  }

  const handleAddNewAddress = async () => {
    setNewAddressLoading(true)
    const result = await saveNewAddress()
    setNewAddressLoading(false)
    if (!result.ok) return
    setOpenAddNewAddress(false)
    router.refresh()
  }

  const productCustomFieldsApplicable = useMemo(() => {
    if (!subscription?.canUseCustomFields) return []
    return products.filter((p: any) => (p.fields?.length || 0) > 0)
  }, [products, subscription])

  const hasOutOfStock = products.some((p: any) => p.quantity === 0 || p.isOutOfStock)
  const canSubmit = !isValidating && canCheckout() && isTermsAccepted && !loading

  const progressCard = (() => {
    const showShipping = freeShippingProgress && !products.every((p: any) => p.isDigital === true)
    const showMinOrder = vendor?.orderMinAmount > 0 && subPrice < vendor.orderMinAmount
    const orderDisc = activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0
      ? (() => {
        const targets = activeOrderDiscount.targets || []
        const target = targets.find((t: any) => t.role === 'target' && t.targetId === null)
          || targets.find((t: any) => t.role === 'target')
          || targets[0]
        const label = target?.valueType === 'percent' && target?.valuePercent
          ? `${target.valuePercent}%-ийн хөнгөлөлт`
          : target?.valueType === 'amount' && target?.valueAmount
            ? `${formatPrice(target.valueAmount)} хөнгөлөлт`
            : activeOrderDiscount.valueType === 'percent' && activeOrderDiscount.valuePercent
              ? `${activeOrderDiscount.valuePercent}%-ийн хөнгөлөлт`
              : activeOrderDiscount.valueType === 'amount' && activeOrderDiscount.valueAmount
                ? `${formatPrice(activeOrderDiscount.valueAmount)} хөнгөлөлт`
                : activeOrderDiscount.name || 'Захиалгын хөнгөлөлт'
        return { minAmt: activeOrderDiscount.minOrderAmount, label, pct: Math.min((subPrice / activeOrderDiscount.minOrderAmount) * 100, 100), reached: subPrice >= activeOrderDiscount.minOrderAmount }
      })()
      : null
    if (!showShipping && !showMinOrder && !orderDisc) return null
    return (
      <div className="rounded-xl px-5 py-4 space-y-3" style={cardStyle}>
        {showShipping && (
          <ProgressRow
            label={freeShippingProgress.reached
              ? <span className="font-medium" style={{ color: '#34d399' }}>Үнэгүй хүргэлт хангагдсан</span>
              : <span className="text-white">Үнэгүй хүргэлтэд <span className="font-semibold tabular-nums">{formatPrice(freeShippingProgress.remaining)}</span> үлдсэн</span>}
            pct={freeShippingProgress.percentage}
            barColor="#34d399"
          />
        )}
        {orderDisc && (
          <ProgressRow
            label={orderDisc.reached
              ? <span className="font-medium" style={{ color: '#d8b4fe' }}>{orderDisc.label} хангагдсан{orderDiscountAmount > 0 ? ` · −${formatPrice(orderDiscountAmount)}` : ''}</span>
              : <span className="text-white">{orderDisc.label}-д <span className="font-semibold tabular-nums">{formatPrice(orderDisc.minAmt - subPrice)}</span> үлдсэн</span>}
            pct={orderDisc.pct}
            barColor="#a855f7"
          />
        )}
        {showMinOrder && (
          <ProgressRow
            label={<span className="text-white">Захиалгын доод дүнд <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount - subPrice)}</span> үлдсэн</span>}
            pct={Math.min((subPrice / vendor.orderMinAmount) * 100, 100)}
            barColor="#60a5fa"
          />
        )}
      </div>
    )
  })()

  if (initialLoading) {
    return (
      <div className="cont py-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_400px]">
          <div className="space-y-6">
            <div className="h-10 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="h-44 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="h-44 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
          </div>
          <div className="h-96 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
        </div>
      </div>
    )
  }

  if (products.length === 0 && isPlacingOrder) {
    return (
      <ProcessingState
        className="cont py-20"
        titleClassName="text-white text-[20px] sm:text-[24px]"
        descriptionClassName="text-white/50"
        spinnerClassName="text-white"
      />
    )
  }

  if (products.length === 0) {
    return (
      <div className="cont flex flex-col items-center justify-center py-20">
        <div
          className="w-24 h-24 rounded-full grid place-items-center mb-6"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <ShoppingBag className="w-10 h-10" style={{ color: 'rgba(255,255,255,0.4)' }} strokeWidth={1.5} />
        </div>
        <div className="text-[20px] sm:text-[24px] font-semibold text-white text-center" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
          Таны сагс хоосон байна
        </div>
        <a
          href={navigateUrl('/product', pathname)}
          className="mt-7 inline-flex items-center gap-2 h-12 px-6 rounded-full text-[13.5px] font-semibold transition-opacity hover:opacity-90"
          style={{ background: 'white', color: '#0a0a0c' }}
        >
          <ShoppingBag className="h-4 w-4" /> Дэлгүүр хэсэх
        </a>
      </div>
    )
  }

  const sectionNums: string[] = []
  let n = 0
  const pushNum = () => { n++; sectionNums.push(String(n).padStart(2, '0')); return sectionNums[sectionNums.length - 1] }
  const numContact = pushNum()
  const numProductFields = productCustomFieldsApplicable.length > 0 ? pushNum() : null
  const numOrderFields = subscription?.canUseCustomFields && vendor?.orderFields?.length > 0 ? pushNum() : null
  const numDelivery = showDeliverySection ? pushNum() : null
  const numPayment = pushNum()

  const orderItemsSidebar = (
    <>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-4" style={{ color: '#a8a8b3' }}>
        Захиалга · {cartCount} бараа
      </div>
      <div className="space-y-3">
        {allProducts.map((p: any, idx: number) => {
          const variant = p.variant
          const productData = p.product || p
          const img = variant?.images?.[0] ? formatImageURL(variant.images[0]) : formatImageURL('placeholder.png')
          const qty = p.quantity || 1
          const unit = p.isRewardItem ? getRewardPrice(p) : (variant?.priceSale && variant.priceSale > 0 ? variant.priceSale : variant?.price || 0)
          return (
            <div key={idx} className="flex items-center gap-3">
              <div className="relative w-11 h-11 shrink-0">
                <div
                  className="w-11 h-11 rounded-md overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <img src={img} className="w-full h-full object-cover" alt={productData?.name} />
                </div>
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full inline-flex items-center justify-center text-[9px] font-semibold tabular-nums"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  {qty}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-white line-clamp-1">{productData?.name}</div>
                {p.variant?.name && <div className="text-[10.5px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{p.variant.name}</div>}
              </div>
              <div className="text-[12.5px] tabular-nums shrink-0" style={{ color: unit === 0 ? '#34d399' : 'white' }}>
                {unit === 0 ? 'Үнэгүй' : formatPrice(unit * qty)}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )

  const totalsSection = (
    <div className="pt-4 space-y-2 text-[13px]" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex justify-between gap-3">
        <span style={{ color: 'rgba(255,255,255,0.55)' }}>Барааны үнэ</span>
        <span className="tabular-nums text-white">{formatPrice(subPrice + loyaltySaleOffsetAmount)}</span>
      </div>
      {orderDiscountAmount > 0 && (
        <div className="flex justify-between gap-3">
          <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {activeOrderDiscount?.name ? `Захиалгын · ${activeOrderDiscount.name}` : 'Захиалгын хөнгөлөлт'}
          </span>
          <span className="tabular-nums shrink-0" style={{ color: '#d8b4fe' }}>− {formatPrice(orderDiscountAmount)}</span>
        </div>
      )}
      {validCouponDiscount > 0 && (
        <div className="flex justify-between gap-3">
          <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Купон{appliedCoupon?.discount?.name ? ` · ${appliedCoupon.discount.name}` : ''}
          </span>
          <span className="tabular-nums shrink-0" style={{ color: '#fda4af' }}>− {formatPrice(validCouponDiscount)}</span>
        </div>
      )}
      {validGiftCardDiscount > 0 && (
        <div className="flex justify-between gap-3">
          <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>Бэлгийн карт</span>
          <span className="tabular-nums shrink-0" style={{ color: '#d8b4fe' }}>− {formatPrice(validGiftCardDiscount)}</span>
        </div>
      )}
      {loyaltyDiscount > 0 && (
        <div className="flex justify-between gap-3">
          <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>Лоялти хөнгөлөлт</span>
          <span className="tabular-nums shrink-0" style={{ color: '#34d399' }}>− {formatPrice(loyaltyDiscount + loyaltySaleOffsetAmount)}</span>
        </div>
      )}
      {validLoyaltyVoucherDiscount > 0 && (
        <div className="flex justify-between gap-3">
          <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>Лоялти ваучер</span>
          <span className="tabular-nums shrink-0" style={{ color: '#34d399' }}>− {formatPrice(validLoyaltyVoucherDiscount)}</span>
        </div>
      )}
      {selectedDelivery === 'delivery' && showDeliverySection && (
        <div className="flex justify-between gap-3">
          <span style={{ color: 'rgba(255,255,255,0.55)' }}>Хүргэлт</span>
          <span className="tabular-nums" style={{ color: vendor?.deliveryType === 'laterDelivery' ? '#fbbf24' : deliveryPrice === 0 ? '#34d399' : 'white' }}>
            {vendor?.deliveryType === 'laterDelivery' ? 'Нэмэлтээр тооцно' : deliveryPrice === 0 ? 'Үнэгүй' : formatPrice(deliveryPrice)}
          </span>
        </div>
      )}
    </div>
  )

  const grandTotal = (
    <div className="pt-4 flex items-baseline justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <span className="text-[13px] text-white font-semibold">Нийт</span>
      <span
        className="font-semibold tabular-nums text-[22px]"
        style={{ fontFamily: 'Gilroy, Inter, sans-serif', color: totalPrice <= 0 ? '#34d399' : 'white' }}
      >
        {totalPrice <= 0 ? 'Үнэгүй' : formatPrice(totalPrice)}
      </span>
    </div>
  )

  const termsCheckbox = (
    <button
      type="button"
      onClick={() => setIsTermsAccepted(!isTermsAccepted)}
      className="flex items-start gap-2.5 text-[12px] cursor-pointer leading-snug text-left w-full"
      style={{ color: 'rgba(255,255,255,0.65)' }}
    >
      <span
        className="w-4 h-4 rounded inline-flex items-center justify-center shrink-0 mt-0.5"
        style={isTermsAccepted
          ? { background: 'white', border: '1px solid white' }
          : { background: 'transparent', border: '1px solid rgba(255,255,255,0.3)' }}
      >
        {isTermsAccepted && <Check className="h-3 w-3" style={{ color: '#0a0a0c' }} strokeWidth={3.5} />}
      </span>
      <span>
        <a onClick={(e) => { e.stopPropagation(); handleTermsClick('service') }} className="text-white underline cursor-pointer">Үйлчилгээ</a>
        {', '}
        <a onClick={(e) => { e.stopPropagation(); handleTermsClick('delivery') }} className="text-white underline cursor-pointer">Хүргэлт</a>
        {', '}
        <a onClick={(e) => { e.stopPropagation(); handleTermsClick('payment') }} className="text-white underline cursor-pointer">Төлбөр</a>
        {' нөхцөлтэй танилцсан.'}
      </span>
    </button>
  )

  const submitButton = (
    <button
      type="button"
      onClick={submitCheckout}
      disabled={!canSubmit}
      className="w-full rounded-full text-[14px] font-semibold inline-flex items-center justify-center gap-2 cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ background: 'white', color: '#0a0a0c', height: 52 }}
    >
      {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
      {effectiveDeposit > 0 ? 'Урьдчилгаа төлөх' : 'Төлөх'} · {totalPrice <= 0 ? 'Үнэгүй' : formatPrice(effectiveDeposit > 0 ? effectiveDeposit : totalPrice)}
    </button>
  )

  return (
    <div>
      <TermsDrawer isOpen={termsDrawerOpen} onClose={() => setTermsDrawerOpen(false)} type={selectedTermsType} terms={vendor?.terms || {}} vendor={vendor} />

      <AddAddressModal
        open={openAddNewAddress}
        onClose={() => setOpenAddNewAddress(false)}
        onSave={handleAddNewAddress}
        loading={newAddressLoading}
        addressApi={addressApi}
        vendor={vendor}
      />

      <div className="cont pt-10 pb-6">
        <a
          href={navigateUrl('/cart', pathname)}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-opacity hover:opacity-80"
          style={{ color: 'rgba(255,255,255,0.65)' }}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Сагс руу буцах
        </a>
        <h1
          className="mt-4 font-semibold tracking-[-0.025em] leading-[0.95] text-white"
          style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(32px, 5vw, 44px)' }}
        >
          Захиалга
        </h1>
      </div>

      {vendor?.isDanVerified === false && (
        <div className="cont pb-4">
          <div
            className="flex items-start gap-3 p-3 rounded-xl"
            style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)' }}
          >
            <Info className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#fcd34d' }} />
            <div className="text-[12px] leading-snug" style={{ color: 'rgba(252,211,77,0.85)' }}>
              <span className="font-bold">{vendor?.name}</span> дэлгүүр <span className="font-bold">Улсын бүртгэлээр баталгаажаагүй</span> тул нөхцөлтэй сайтар танилцаад захиалга өгнө үү.
            </div>
          </div>
        </div>
      )}

      <div className="cont pb-16">
        <div className="grid gap-8 lg:gap-10 grid-cols-1 lg:grid-cols-[1fr_400px] items-start">
          <div className="space-y-10 min-w-0">
            <Section num={numContact} title="Холбоо барих">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <DarkInput label="Нэр" placeholder="Таны нэр" value={fullName} onChange={(e: any) => setFullName(e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <DarkInput label="И-мэйл хаяг" type="email" placeholder="example@shop.mn" value={email} onChange={(e: any) => setEmail(e.target.value)} />
                </div>
                <DarkInput label="Утасны дугаар" required placeholder="00000000" value={phone} onChange={(e: any) => setPhone(e.target.value)} />
                <DarkInput label="Нэмэлт утас" placeholder="00000000" value={additionalPhone} onChange={(e: any) => setAdditionalPhone(e.target.value)} />
              </div>
            </Section>

            {numProductFields && (
              <Section num={numProductFields} title="Бүтээгдэхүүний мэдээлэл">
                <div className="space-y-3">
                  {productCustomFieldsApplicable.map((p: any) => {
                    const productData = p.product || p
                    const fields = p.fields || []
                    return (
                      <div key={p.id} className="rounded-xl overflow-hidden" style={cardStyle}>
                        <div className="w-full px-4 py-3 flex items-center gap-3 text-left">
                          <div
                            className="w-10 h-10 rounded-md overflow-hidden shrink-0"
                            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
                          >
                            {p.variant?.images?.[0] ? (
                              <img src={formatImageURL(p.variant.images[0])} alt={productData.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full grid place-items-center">
                                <Package className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-white line-clamp-2">{productData.name}</div>
                            <div className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                              Шаардагдах {fields.length} нэмэлт мэдээлэл
                            </div>
                          </div>
                        </div>
                        {(
                          <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                            <div className="pt-4" />
                            {fields.map((field: any, i: number) => (
                              <CustomFieldRenderer
                                key={i}
                                field={field}
                                fieldKey={`${p.id}-${field.name}`}
                                value={customFieldValues[`${p.id}-${field.name}`] || field.value || ''}
                                onChange={(v) => setProductField(`${p.id}-${field.name}`, v)}
                                onFile={(file) => uploadProductFile(`${p.id}-${field.name}`, file)}
                                uploading={uploadingFiles.has(`${p.id}-${field.name}`)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </Section>
            )}

            {numOrderFields && (
              <Section num={numOrderFields} title="Захиалгын мэдээлэл">
                <div className="space-y-3 rounded-xl p-5" style={cardStyle}>
                  {vendor.orderFields.map((field: any, i: number) => (
                    <CustomFieldRenderer
                      key={i}
                      field={field}
                      fieldKey={field.name}
                      value={orderCustomFieldValues[field.name] || field.value || ''}
                      onChange={(v) => setOrderField(field.name, v)}
                      onFile={(file) => uploadOrderFile(field.name, file)}
                      uploading={uploadingOrderFiles.has(field.name)}
                      orderLevel
                    />
                  ))}
                </div>
              </Section>
            )}

            {numDelivery && (
              <Section num={numDelivery} title="Хүргэлтийн арга">
                <div className="space-y-2">
                  {hasDeliveryOption && (
                    <Radio
                      active={selectedDelivery === 'delivery'}
                      onClick={() => selectDelivery('delivery')}
                      title={vendor?.isBooklo
                        ? <span className="inline-flex items-center gap-2"><Image src={Booklo} alt="Booklo" width={18} height={18} className="w-[18px] h-[18px] rounded object-contain" />Хүргэлтээр авах</span>
                        : 'Хүргэлтээр авах'}
                      sub={vendor?.isBooklo ? "Booklo хүргэлт 6'500₮" : vendor?.deliveryType === 'laterDelivery' ? 'Үнэ тусдаа тооцогдоно' : (deliveryRowPrice === 0 ? 'Үнэгүй' : `${formatPrice(deliveryRowPrice)} хүргэлт`)}
                      right={
                        <span className="text-[12px] tabular-nums shrink-0" style={{ color: vendor?.deliveryType === 'laterDelivery' ? '#fbbf24' : deliveryRowPrice === 0 ? '#34d399' : 'white' }}>
                          {vendor?.deliveryType === 'laterDelivery' ? 'Нэмэлтээр тооцно' : deliveryRowPrice === 0 ? 'Үнэгүй' : formatPrice(deliveryRowPrice)}
                        </span>
                      }
                    />
                  )}
                  {hasPickupOption && (
                    <Radio
                      active={selectedDelivery === 'pickup'}
                      onClick={() => selectDelivery('pickup')}
                      title="Очиж авах"
                      sub="Салбараас ирж авна"
                      right={<span className="text-[12px] shrink-0" style={{ color: '#34d399' }}>Үнэгүй</span>}
                    />
                  )}
                </div>

                {selectedDelivery === 'delivery' && hasDeliveryOption && (vendor?.isBooklo || !vendor?.canDeliverCountryside) && (
                  <div className="mt-4 p-3 rounded-xl text-[12px]" style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', color: '#fbbf24' }}>
                    <strong className="font-semibold">Анхаар:</strong> {vendor?.isBooklo ? 'Зөвхөн Booklo хүргэлтийн бүсэд багтах дүүрэг, хороо сонгогдоно.' : 'Манай дэлгүүр зөвхөн Улаанбаатар хот дотор хүргэлт хийнэ.'}
                  </div>
                )}

                {selectedDelivery === 'delivery' && hasDeliveryOption && (
                  <div className="mt-5">
                    <div className="text-[12px] mb-2.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Хүргэлтийн хаяг</div>
                    {session?.user?.id ? (
                      <div className="space-y-2">
                        {customerAddress?.length > 0 && customerAddress.map((a: any) => {
                          const disabled = (!vendor?.canDeliverCountryside && a.city !== 'Улаанбаатар') || !isAddressDeliverable(a)
                          const active = selectedAddress === a.id
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => {
                                if (disabled) return
                                setSelectedAddress(a.id)
                                setAddressObj({
                                  city: a.city, duureg: a.duureg, khoroo: a.khoroo, bair: a.bair, toot: a.toot,
                                  additionalAddress: a.additionalAddress, type: a.type, houseNum: a.houseNum, street: a.street,
                                  building: a.building, floor: a.floor, officeName: a.officeName,
                                })
                              }}
                              disabled={disabled}
                              className="w-full text-left p-4 rounded-xl flex items-start justify-between gap-3 transition-all cursor-pointer disabled:cursor-not-allowed"
                              style={{
                                background: active ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.3)',
                                border: active ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.13)',
                                opacity: disabled ? 0.45 : 1,
                              }}
                            >
                              <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                                <AddressTypeBadge type={a.type || 'apartment'} />
                                <div className="text-[13px] font-semibold text-white">
                                  {a.city}, {a.duureg}, {a.khoroo}
                                  {disabled && (
                                    <span
                                      className="ml-2 inline-flex text-[10px] font-semibold tabular-nums uppercase px-1.5 py-0.5 rounded"
                                      style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}
                                    >
                                      Хүргэлт боломжгүй
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11.5px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                                  {a.type === 'apartment' && `${a.bair}-р байр, тоот ${a.toot}`}
                                  {a.type === 'house' && `${a.street ? `${a.street}, ` : ''}${a.houseNum}`}
                                  {a.type === 'office' && `${a.building}, ${a.floor}-р давхар, тоот ${a.toot}${a.officeName ? ` (${a.officeName})` : ''}`}
                                  {!a.type && `${a.bair}-р байр, тоот ${a.toot}`}
                                </div>
                                {a.additionalAddress && (
                                  <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{a.additionalAddress}</div>
                                )}
                              </div>
                              {active && !disabled && (
                                <CircleCheck className="h-4 w-4 shrink-0 mt-1" style={{ color: 'white' }} />
                              )}
                            </button>
                          )
                        })}
                        <button
                          type="button"
                          onClick={() => setOpenAddNewAddress(true)}
                          className="w-full h-11 rounded-xl text-[12.5px] font-medium inline-flex items-center justify-center gap-2 cursor-pointer transition-colors"
                          style={{ background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px dashed rgba(255,255,255,0.2)' }}
                        >
                          <MapPinPlus className="h-3.5 w-3.5" />
                          Шинэ хаяг нэмэх
                        </button>
                        {customerAddress?.length > 0 && customerAddress.every((a: any) => !isAddressDeliverable(a)) && (
                          <div
                            className="p-3 rounded-xl text-[11.5px]"
                            style={{ background: 'rgba(244,63,94,0.06)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.25)' }}
                          >
                            Таны хадгалсан хаягууд руу хүргэлт хийх боломжгүй байна. Шинэ хаяг нэмнэ үү.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <AddressTypeTabs value={addressType} onChange={setAddressType} />
                        <DarkSelect
                          label="Хот / Аймаг"
                          required
                          value={selectedProvince || ''}
                          items={!vendor?.canDeliverCountryside ? provinces.filter((p: any) => p.value === 'Улаанбаатар') : provinces}
                          onChange={(v: string) => { setSelectedProvince(v); setCity(v); setSelectedDistrict(null); setDuureg(''); setSelectedSubdistrict(null); setKhoroo('') }}
                        />
                        <DarkSelect
                          label="Дүүрэг / Сум"
                          required
                          value={selectedDistrict || ''}
                          items={districts}
                          onChange={(v: string) => { setSelectedDistrict(v); setDuureg(v); setSelectedSubdistrict(null); setKhoroo('') }}
                        />
                        <DarkSelect
                          label="Хороо / Баг"
                          required
                          value={selectedSubdistrict || ''}
                          items={subdistricts}
                          onChange={(v: string) => { setSelectedSubdistrict(v); setKhoroo(v) }}
                        />
                        {addressType === 'apartment' && (
                          <div className="grid grid-cols-2 gap-3">
                            <DarkInput label="Байр" required placeholder="Байрны дугаар" value={bair} onChange={(e: any) => setBair(e.target.value)} />
                            <DarkInput label="Тоот" required placeholder="Хаалганы тоот" value={toot} onChange={(e: any) => setToot(e.target.value)} />
                          </div>
                        )}
                        {addressType === 'house' && (
                          <div className="grid grid-cols-2 gap-3">
                            <DarkInput label="Хашааны дугаар" required placeholder="Хашаа №" value={houseNum} onChange={(e: any) => setHouseNum(e.target.value)} />
                            <DarkInput label="Гудамж" placeholder="Гудамжны нэр" value={street} onChange={(e: any) => setStreet(e.target.value)} />
                          </div>
                        )}
                        {addressType === 'office' && (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <DarkInput label="Барилга" required placeholder="Барилгын нэр/№" value={building} onChange={(e: any) => setBuilding(e.target.value)} />
                              <DarkInput label="Давхар" required placeholder="Давхар" value={floor} onChange={(e: any) => setFloor(e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <DarkInput label="Тоот" required placeholder="Тоот" value={toot} onChange={(e: any) => setToot(e.target.value)} />
                              <DarkInput label="Оффис нэр" placeholder="Оффис" value={officeName} onChange={(e: any) => setOfficeName(e.target.value)} />
                            </div>
                          </>
                        )}
                        <DarkInput label="Нэмэлт мэдээлэл" placeholder="Орц, давхар, орцны код..." value={additionalAddress} onChange={(e: any) => setAdditionalAddress(e.target.value)} />
                      </div>
                    )}
                  </div>
                )}

                {selectedDelivery === 'pickup' && hasPickupOption && (
                  <div className="mt-5">
                    <div className="text-[12px] mb-2.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Салбар сонгох</div>
                    <div className="space-y-2">
                      {branches.map((b: any) => {
                        const active = selectedBranch === b.id
                        return (
                          <button
                            key={b.id}
                            type="button"
                            onClick={() => setSelectedBranch(b.id)}
                            className="w-full text-left p-4 rounded-xl flex items-start gap-3 transition-all cursor-pointer"
                            style={{
                              background: active ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.3)',
                              border: active ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.13)',
                            }}
                          >
                            <Store className="h-4 w-4 mt-0.5 shrink-0" style={{ color: active ? 'white' : 'rgba(255,255,255,0.55)' }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-[13.5px] font-semibold text-white">{b.name}</span>
                                {b.isDefault && (
                                  <span
                                    className="text-[9.5px] font-semibold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded"
                                    style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }}
                                  >
                                    Үндсэн
                                  </span>
                                )}
                              </div>
                              <div className="text-[11.5px] mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>{b.address}</div>
                              {b.phone && <div className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Утас: {b.phone}</div>}
                            </div>
                            {active && <CircleCheck className="h-4 w-4 shrink-0 mt-1" style={{ color: 'white' }} />}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </Section>
            )}

            <Section num={numPayment} title="Төлбөрийн арга">
              {/* Render order mirrors the auto-select precedence in usePayment,
                  so the pre-selected method is always the first row. */}
              <div className="space-y-2">
                <PaymentRow
                  show={canPayOnDelivery && isMethodAllowedForCart('onDelivery')}
                  active={selectedPayment === 'onDelivery'}
                  onClick={() => selectPayment('onDelivery')}
                  icon={<HandCoins className="h-7 w-7 text-white/60" />}
                  title="Барааг авахдаа төлөх"
                  sub="Барааг хүлээн авахдаа шууд төлнө"
                />
                <PaymentRow
                  show={vendor?.qpayMerchant && subscription?.canUseQPay && vendor?.canQpay !== false && isMethodAllowedForCart('qpay')}
                  active={selectedPayment === 'qpay'}
                  onClick={() => selectPayment('qpay')}
                  icon={<Image src={Qpay} alt="QPay" className="h-7 w-7 object-contain" />}
                  title="QPay"
                  sub="QR код уншуулж төлөх"
                />
                <PaymentRow
                  show={vendor?.canTransfer && vendor?.canSimple !== false && isMethodAllowedForCart('transfer')}
                  active={selectedPayment === 'transfer'}
                  onClick={() => selectPayment('transfer')}
                  icon={<Image src={TransferIcon} alt="Transfer" className="h-7 w-7 object-contain" />}
                  title="Дансаар шилжүүлэх"
                  sub="Дэлгүүрийн данс руу шилжүүлэг"
                />
                <PaymentRow
                  show={vendor?.storepayCredentials && vendor?.canStorepay !== false && isMethodAllowedForCart('storepay')}
                  active={selectedPayment === 'storepay'}
                  disabled={totalPrice < 100000}
                  onClick={() => totalPrice >= 100000 && selectPayment('storepay')}
                  icon={<Image src={Storepay} alt="StorePay" className="h-7 w-7 object-cover rounded" />}
                  title="StorePay"
                  sub={totalPrice >= 100000 ? '4-6 хуваан төлөх' : 'Зөвхөн 100,000₮+ захиалгад'}
                  expanded={selectedPayment === 'storepay' && totalPrice >= 100000 ? (
                    <DarkInput placeholder="StorePay бүртгэлтэй утасны дугаар" value={storepayPhone} onChange={(e: any) => setStorepayPhone(e.target.value)} required />
                  ) : null}
                />
                <PaymentRow
                  show={vendor?.pocketCredentials && vendor?.canPocket !== false && isMethodAllowedForCart('pocket')}
                  active={selectedPayment === 'pocket'}
                  disabled={totalPrice < 100000}
                  onClick={() => totalPrice >= 100000 && selectPayment('pocket')}
                  icon={<Image src={Pocket} alt="Pocket" className="h-7 w-7 object-cover rounded" />}
                  title="PocketZero"
                  sub={totalPrice >= 100000 ? '0% хүүтэй, 2-6 хуваан төлөх' : 'Зөвхөн 100,000₮+ захиалгад'}
                />
                <PaymentRow
                  show={vendor?.lendCredentials && vendor?.canLend !== false && isMethodAllowedForCart('lend')}
                  active={selectedPayment === 'lend'}
                  disabled={totalPrice < 100000}
                  onClick={() => totalPrice >= 100000 && selectPayment('lend')}
                  icon={<Image src={Lend} alt="Lend" className="h-7 w-7 object-contain" />}
                  title="LendMN"
                  sub={totalPrice >= 100000 ? '30-60 хоногт хуваан төлөх' : 'Зөвхөн 100,000₮+ захиалгад'}
                  expanded={selectedPayment === 'lend' && totalPrice >= 100000 ? (
                    <DarkInput placeholder="LendMN бүртгэлтэй утасны дугаар" value={lendPhone} onChange={(e: any) => setLendPhone(e.target.value)} required />
                  ) : null}
                />
                <PaymentRow
                  show={vendor?.omniwayCredentials && vendor?.canOmniway !== false && isMethodAllowedForCart('omniway')}
                  active={selectedPayment === 'omniway'}
                  disabled={totalPrice < 100000}
                  onClick={() => totalPrice >= 100000 && selectPayment('omniway')}
                  icon={<Image src={Omniway} alt="OmniWay" className="h-7 w-7 object-cover rounded" />}
                  title="OmniWay"
                  sub={totalPrice >= 100000 ? 'Хуваан төлөх боломжтой' : 'Зөвхөн 100,000₮+ захиалгад'}
                />
              </div>
            </Section>

            {buyXGetYInfo?.map((info: any) => info.qualifies && info.rewardProducts?.length > 0 && (() => {
              const { totalRemaining } = getBuyXGetYRewardState(info, info.rewardProducts[0])
              if (totalRemaining <= 0) return null
              return (
                <div
                  key={info.discount.id}
                  className="rounded-xl p-4"
                  style={{ background: 'rgba(251,146,60,0.06)', border: '1px dashed rgba(251,146,60,0.4)' }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                      style={{ background: 'rgba(251,146,60,0.16)', border: '1px solid rgba(251,146,60,0.3)', color: '#fb923c' }}
                    >
                      <Gift className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold text-white">{info.discount?.name || 'Хөнгөлөлт'}</div>
                      <div className="text-[11.5px] mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        {getBuyXTriggerCount(info.discount)} авбал {getBuyXGetYRewardRule(info)}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px]" style={{ color: '#6ee7b7' }}>
                        <Check className="w-3 h-3" />
                        <span className="font-medium">Нөхцөл хангагдлаа · {totalRemaining} сонгох эрхтэй</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 mt-3">
                    {info.rewardProducts.map((reward: any) => {
                      const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
                      if (isLockedByOther) return null
                      return (
                        <div
                          key={reward.id}
                          className="flex items-center gap-3 p-3 rounded-lg"
                          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
                        >
                          <div className="w-12 h-12 shrink-0 rounded-md overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)' }}>
                            <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="text-[13px] font-medium text-white truncate">{reward.name}</div>
                              {reward.valueType === 'free' ? (
                                <span className="text-[9.5px] font-semibold uppercase px-1.5 py-0.5 rounded shrink-0" style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}>Үнэгүй</span>
                              ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                <span className="text-[9.5px] font-semibold tabular-nums px-1.5 py-0.5 rounded shrink-0" style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}>−{reward.valuePercent}%</span>
                              ) : null}
                            </div>
                            <div className="mt-0.5 text-[12px] tabular-nums" style={{ color: rewardPrice === 0 ? '#34d399' : 'white' }}>
                              {rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => addRewardItem({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase, variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock } }, 'buyXgetY')}
                            disabled={!canAdd || rewardLoading}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11.5px] font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                            style={canAdd && !isOutOfStock ? { background: 'white', color: '#0a0a0c' } : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}
                          >
                            {isOutOfStock ? 'Дууссан' : canAdd ? 'Нэмэх' : 'Нэмэгдсэн'}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })())}

            {(availableOrderRewardItems?.length > 0 || availableCouponRewardItems?.length > 0) && (
              <div
                className="rounded-xl p-4"
                style={{ background: 'rgba(251,146,60,0.06)', border: '1px dashed rgba(251,146,60,0.4)' }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                    style={{ background: 'rgba(251,146,60,0.16)', border: '1px solid rgba(251,146,60,0.3)', color: '#fb923c' }}
                  >
                    <Gift className="w-4 h-4" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-[13.5px] font-semibold text-white">Захиалгын бэлэг</div>
                    <div className="text-[11.5px] mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Захиалгын хөнгөлөлтийн бэлэг</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {[...(availableOrderRewardItems || []), ...(availableCouponRewardItems || [])].map((reward: any) => {
                    const out = isRewardOutOfStock(reward)
                    const rp = getRewardPrice(reward)
                    return (
                      <div
                        key={reward.id}
                        className="flex items-center gap-3 p-3 rounded-lg"
                        style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
                      >
                        <div className="w-12 h-12 shrink-0 rounded-md overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)' }}>
                          <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-white truncate">{reward.name}</div>
                          <div className="mt-0.5 text-[12px] tabular-nums" style={{ color: rp === 0 ? '#34d399' : 'white' }}>
                            {rp === 0 ? 'Үнэгүй' : formatPrice(rp)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => addRewardItem(reward, 'order')}
                          disabled={out || rewardLoading}
                          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11.5px] font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                          style={out ? { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' } : { background: 'white', color: '#0a0a0c' }}
                        >
                          {out ? 'Дууссан' : 'Нэмэх'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="hidden lg:flex lg:sticky lg:top-32 self-start flex-col gap-4 min-w-0">
            {progressCard}
            <aside className="rounded-2xl p-6 space-y-5" style={cardStyle}>
            {orderItemsSidebar}
            <DiscountTabs vendor={vendor} />
            {totalsSection}
            {grandTotal}
            {hasOutOfStock && (
              <div
                className="rounded-xl p-3 flex items-start gap-2"
                style={{ background: 'rgba(244,63,94,0.06)', border: '1px solid rgba(244,63,94,0.25)' }}
              >
                <PackageX className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#fda4af' }} />
                <div className="text-[11.5px]" style={{ color: '#fda4af' }}>Сагсанд дууссан бараа байна</div>
              </div>
            )}
            {termsCheckbox}
            {submitButton}
          </aside>
          </div>
        </div>

        <div className="lg:hidden mt-10">
          <button
            type="button"
            onClick={() => setShowMobileSummary(!showMobileSummary)}
            className="w-full flex items-center justify-between gap-3 p-4 rounded-xl cursor-pointer"
            style={cardStyle}
          >
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-white" />
              <span className="text-[13px] font-medium text-white">Захиалгын дэлгэрэнгүй</span>
              <span className="text-[12px] tabular-nums" style={{ color: totalPrice <= 0 ? '#34d399' : 'rgba(255,255,255,0.55)' }}>· {totalPrice <= 0 ? 'Үнэгүй' : formatPrice(totalPrice)}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${showMobileSummary ? 'rotate-180' : ''}`} style={{ color: 'rgba(255,255,255,0.5)' }} />
          </button>
          {showMobileSummary && (
            <div className="mt-3 rounded-2xl p-6 space-y-5" style={cardStyle}>
              {orderItemsSidebar}
              <DiscountTabs vendor={vendor} />
              {totalsSection}
              {grandTotal}
              {allProducts.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  {allProducts.map((p: any, i: number) => (<CheckoutCard key={i} product={p} />))}
                </div>
              )}
            </div>
          )}
          <div className="mt-4 space-y-3">
            {termsCheckbox}
            {submitButton}
          </div>
        </div>
      </div>
    </div>
  )
}

function PaymentRow({ show, active, disabled, onClick, icon, title, sub, expanded }: { show?: boolean, active?: boolean, disabled?: boolean, onClick: () => void, icon: React.ReactNode, title: string, sub: string, expanded?: React.ReactNode }) {
  if (!show) return null
  return (
    <div>
      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className="w-full text-left flex items-center gap-3 p-4 rounded-xl transition-colors cursor-pointer disabled:cursor-not-allowed"
        style={{
          background: active ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.3)',
          border: active ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.13)',
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <span
          className="w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0"
          style={active
            ? { background: 'white', border: '4px solid white', outline: '1px solid rgba(255,255,255,0.4)', outlineOffset: 2 }
            : { border: '1.5px solid rgba(255,255,255,0.3)' }}
        />
        <div className="shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-medium text-white">{title}</div>
          <div className="text-[11.5px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{sub}</div>
        </div>
      </button>
      {expanded && <div className="mt-2">{expanded}</div>}
    </div>
  )
}

function CustomFieldRenderer({ field, fieldKey, value, onChange, onFile, uploading, orderLevel }: { field: any, fieldKey: string, value: any, onChange: (v: any) => void, onFile: (f?: File) => void, uploading: boolean, orderLevel?: boolean }) {
  if (field.type === 'text' || field.type === 'number') {
    return (
      <DarkInput
        label={field.name}
        required={field.required}
        type={field.type}
        placeholder={`${field.name} оруулна уу`}
        value={value}
        onChange={(e: any) => onChange(e.target.value)}
      />
    )
  }
  if (field.type === 'textarea') {
    return (
      <DarkTextarea
        label={field.name}
        required={field.required}
        rows={3}
        placeholder={`${field.name} оруулна уу`}
        value={value}
        onChange={(e: any) => onChange(e.target.value)}
      />
    )
  }
  if (field.type === 'select') {
    const items = (field.options || []).map((o: string) => ({ label: o, value: o }))
    return (
      <DarkSelect
        label={field.name}
        required={field.required}
        items={items}
        value={value}
        onChange={(v: string) => onChange(v)}
      />
    )
  }
  if (field.type === 'checkbox') {
    return (
      <button
        type="button"
        onClick={() => onChange(value === 'true' ? 'false' : 'true')}
        className="w-full text-left rounded-xl p-3 cursor-pointer transition-colors flex items-start gap-3"
        style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.13)' }}
      >
        <span
          className="shrink-0 mt-0.5 h-4 w-4 rounded grid place-items-center"
          style={value === 'true'
            ? { background: 'white', border: '1px solid white' }
            : { background: 'transparent', border: '1px solid rgba(255,255,255,0.3)' }}
        >
          {value === 'true' && <Check className="h-3 w-3" style={{ color: '#0a0a0c' }} strokeWidth={3.5} />}
        </span>
        <div className="text-[13px] text-white">
          {field.name}{field.required && <span style={{ color: '#fda4af' }}> *</span>}
        </div>
      </button>
    )
  }
  if (field.type === 'date') {
    const dv = value ? (typeof value === 'string' ? new Date(value) : value) : null
    const dStr = dv ? new Date(dv.getTime() - dv.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''
    return (
      <DarkInput
        label={field.name}
        required={field.required}
        type="datetime-local"
        value={dStr}
        onChange={(e: any) => onChange(e.target.value ? new Date(e.target.value).toISOString() : '')}
        style={{ colorScheme: 'dark' }}
      />
    )
  }
  if (field.type === 'productImage') {
    const picked: string[] = Array.isArray(value) ? value : value ? [value] : []
    const toggle = (image: string) => {
      if (!field.multiple) return onChange(picked[0] === image ? '' : image)
      onChange(picked.includes(image) ? picked.filter((img: string) => img !== image) : [...picked, image])
    }
    return (
      <div>
        <FieldLabel required={field.required}>{field.name}</FieldLabel>
        <p className="text-[11px] mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {field.multiple ? 'Дарж сонгоно уу (хэд хэдийг сонгож болно)' : 'Дарж нэг зургийг сонгоно уу'}
        </p>
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
          {(field.options || []).map((image: string) => {
            const isSelected = picked.includes(image)
            return (
              <button
                key={image}
                type="button"
                onClick={() => toggle(image)}
                aria-pressed={isSelected}
                title={isSelected ? 'Сонгосон' : 'Сонгох'}
                className="aspect-square relative rounded-xl overflow-hidden cursor-pointer transition-opacity"
                style={{
                  border: isSelected ? '2px solid white' : '2px solid rgba(255,255,255,0.2)',
                  boxShadow: isSelected ? '0 0 0 2px rgba(255,255,255,0.25)' : 'none',
                  opacity: isSelected ? 1 : 0.75,
                }}
              >
                <img src={formatImageURL(image)} alt="" className="w-full h-full object-cover" />
                {isSelected && <span className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.15)' }} />}
                <span
                  className="absolute top-1 right-1 h-5 w-5 rounded-full grid place-items-center"
                  style={{ background: isSelected ? 'white' : 'rgba(0,0,0,0.35)', border: isSelected ? '1px solid rgba(0,0,0,0.2)' : '1px solid rgba(255,255,255,0.7)' }}
                >
                  <Check className="h-3 w-3" style={{ color: isSelected ? '#0a0a0c' : 'transparent' }} strokeWidth={3.5} />
                </span>
              </button>
            )
          })}
        </div>
        {picked.length > 0 && (
          <p className="text-[11px] mt-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {field.multiple ? `${picked.length} зураг сонгосон` : 'Сонгосон'}
          </p>
        )}
      </div>
    )
  }
  if (field.type === 'file') {
    const url = typeof value === 'string' ? value : value?.url
    const isImage = url && url.match(/\.(jpg|jpeg|png|gif|webp)$/i)
    const inputId = orderLevel ? `order-file-${fieldKey}` : `file-${fieldKey}`
    return (
      <div>
        <FieldLabel required={field.required}>{field.name}</FieldLabel>
        <div className="flex items-center gap-3">
          <div
            className="relative w-16 h-16 rounded-lg overflow-hidden cursor-pointer"
            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.13)' }}
            onClick={() => document.getElementById(inputId)?.click()}
          >
            {uploading ? (
              <div className="absolute inset-0 grid place-items-center">
                <LoaderCircle className="h-5 w-5 animate-spin text-white" />
              </div>
            ) : isImage ? (
              <img src={formatImageURL(url)} alt={field.name} className="w-full h-full object-cover" />
            ) : url ? (
              <div className="absolute inset-0 grid place-items-center">
                <FileText className="h-6 w-6" style={{ color: 'rgba(255,255,255,0.5)' }} />
              </div>
            ) : (
              <div className="absolute inset-0 grid place-items-center">
                <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Файл</span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => document.getElementById(inputId)?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-medium cursor-pointer transition-colors disabled:opacity-60"
            style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
          >
            {uploading ? 'Хуулж байна' : url ? 'Солих' : 'Файл сонгох'}
          </button>
          <input
            id={inputId}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f) }}
          />
        </div>
      </div>
    )
  }
  return null
}

function AddAddressModal({ open, onClose, onSave, loading, addressApi, vendor }: { open: boolean, onClose: () => void, onSave: () => void, loading: boolean, addressApi: any, vendor: any }) {
  if (!open) return null
  const {
    addressType, setAddressType, bair, setBair, toot, setToot, additionalAddress, setAdditionalAddress,
    houseNum, setHouseNum, street, setStreet, building, setBuilding, floor, setFloor, officeName, setOfficeName,
    selectedProvince, setSelectedProvince, selectedDistrict, setSelectedDistrict, selectedSubdistrict, setSelectedSubdistrict,
    provinces, districts, subdistricts, setCity, setDuureg, setKhoroo,
  } = addressApi

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" />
      <div
        className="relative w-full sm:max-w-140 max-h-[90vh] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-3xl"
        style={{
          background: 'linear-gradient(180deg, #16161c 0%, #0c0c10 100%)',
          border: '1px solid rgba(255,255,255,0.13)',
          boxShadow: '0 40px 100px -20px rgba(0,0,0,0.75)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5 shrink-0 flex items-start justify-between gap-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Хүргэлтийн хаяг</div>
            <h3 className="mt-1 text-[16px] font-semibold leading-tight text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>Шинэ хаяг оруулах</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-full transition-colors cursor-pointer shrink-0"
            style={{ color: 'rgba(255,255,255,0.5)' }}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto scrollbar-dark space-y-4">
          <AddressTypeTabs value={addressType} onChange={setAddressType} />
          <DarkSelect
            label="Хот / Аймаг"
            required
            value={selectedProvince || ''}
            items={!vendor?.canDeliverCountryside ? provinces.filter((p: any) => p.value === 'Улаанбаатар') : provinces}
            onChange={(v: string) => { setSelectedProvince(v); setCity(v); setSelectedDistrict(null); setDuureg(''); setSelectedSubdistrict(null); setKhoroo('') }}
          />
          <DarkSelect
            label="Дүүрэг / Сум"
            required
            value={selectedDistrict || ''}
            items={districts}
            onChange={(v: string) => { setSelectedDistrict(v); setDuureg(v); setSelectedSubdistrict(null); setKhoroo('') }}
          />
          <DarkSelect
            label="Хороо / Баг"
            required
            value={selectedSubdistrict || ''}
            items={subdistricts}
            onChange={(v: string) => { setSelectedSubdistrict(v); setKhoroo(v) }}
          />
          {addressType === 'apartment' && (
            <div className="grid grid-cols-2 gap-3">
              <DarkInput label="Байр" required placeholder="Байрны дугаар" value={bair} onChange={(e: any) => setBair(e.target.value)} />
              <DarkInput label="Тоот" required placeholder="Хаалганы тоот" value={toot} onChange={(e: any) => setToot(e.target.value)} />
            </div>
          )}
          {addressType === 'house' && (
            <div className="grid grid-cols-2 gap-3">
              <DarkInput label="Хашааны дугаар" required placeholder="Хашаа №" value={houseNum} onChange={(e: any) => setHouseNum(e.target.value)} />
              <DarkInput label="Гудамж" placeholder="Гудамжны нэр" value={street} onChange={(e: any) => setStreet(e.target.value)} />
            </div>
          )}
          {addressType === 'office' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <DarkInput label="Барилга" required placeholder="Барилгын нэр/№" value={building} onChange={(e: any) => setBuilding(e.target.value)} />
                <DarkInput label="Давхар" required placeholder="Давхар" value={floor} onChange={(e: any) => setFloor(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DarkInput label="Тоот" required placeholder="Тоот" value={toot} onChange={(e: any) => setToot(e.target.value)} />
                <DarkInput label="Оффис нэр" placeholder="Оффис" value={officeName} onChange={(e: any) => setOfficeName(e.target.value)} />
              </div>
            </>
          )}
          <DarkInput label="Нэмэлт мэдээлэл" placeholder="Орц, давхар, орцны код..." value={additionalAddress} onChange={(e: any) => setAdditionalAddress(e.target.value)} />
        </div>

        <div className="px-6 py-4 shrink-0 flex items-center justify-end gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.3)' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="h-11 px-5 rounded-full text-[13px] font-medium cursor-pointer transition-colors disabled:opacity-50"
            style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
          >
            Болих
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={loading}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-[13px] font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: 'white', color: '#0a0a0c' }}
          >
            {loading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            {loading ? 'Хадгалж байна' : 'Хадгалах'}
          </button>
        </div>
      </div>
    </div>
  )
}
